import React from 'react';
import { cn } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faVolumeXmark, faVolumeHigh, faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';

export type VideoCardProps = {
  src: string;
  poster?: string;
  isDarkMode?: boolean;
  onFullscreen?: () => void;
  className?: string;
};

export const VideoCard: React.FC<VideoCardProps> = ({ src, poster, isDarkMode, onFullscreen, className }) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [current, setCurrent] = React.useState(0);
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showUi, setShowUi] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
    } else {
      // lazy set src only when playing to avoid auto-load
      if (v.src !== src) v.src = src;
      v.play().then(() => setIsPlaying(true)).catch(() => {
        setError('Playback blocked');
      });
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v) return;
    const val = parseFloat(e.target.value);
    v.currentTime = val;
    setCurrent(val);
  };

  const timeFmt = (t: number) => {
    if (!isFinite(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div
      ref={rootRef}
      className={cn('group relative w-full aspect-video rounded-xl overflow-hidden select-none', className)}
      onMouseEnter={() => setShowUi(true)}
      onMouseLeave={() => setShowUi(false)}
    >
      <div className="absolute inset-0 bg-gray-900/5" />
      <video
        ref={videoRef}
        poster={poster}
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          setDuration(v.duration || 0);
          setReady(true);
          setIsMuted(v.muted);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onEnded={() => setIsPlaying(false)}
        onError={() => setError('Unable to play')}
      />

      {/* Controls overlay */}
      <div
        className={cn(
          'absolute inset-0 grid grid-rows-[1fr_auto] pointer-events-none transition-opacity duration-200',
          showUi ? 'opacity-100' : 'opacity-0',
          'group-hover:opacity-100'
        )}
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.0))' }}>
        <div className="flex items-center justify-center">
          <button
            type="button"
            className="pointer-events-auto inline-flex bg-[#30303090] items-center justify-center rounded-full w-12 h-12 text-white"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <FontAwesomeIcon icon={faPause} className="w-6 h-6" />
            ) : (
              <FontAwesomeIcon icon={faPlay} className="w-6 h-6" />
            )}
          </button>
        </div>
        <div className="pointer-events-auto px-[5px] pb-3 flex items-center gap-2 text-white">
          <button
            type="button"
            className="p-2 rounded-md text-white"
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <FontAwesomeIcon icon={faVolumeXmark} className="w-4 h-4" />
            ) : (
              <FontAwesomeIcon icon={faVolumeHigh} className="w-4 h-4" />
            )}
          </button>
          <input
            type="range"
            min={0}
            max={Math.max(1, duration)}
            step={0.1}
            value={current}
            onChange={onSeek}
            className="flex-1 h-1.5 accent-purple-500"
          />
          <div className="text-xs tabular-nums w-[64px] text-right">
            {timeFmt(current)} / {timeFmt(duration)}
          </div>
          <button
            type="button"
            className="p-2 rounded-md text-white"
            onClick={onFullscreen}
            aria-label="Fullscreen"
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error badge (but still allow fullscreen to try) */}
      {error && (
        <div className="absolute top-2 right-2 text-xs px-2 py-1 rounded bg-red-500/80 text-white">{error}</div>
      )}
    </div>
  );
};

export default VideoCard;
